import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Shield, 
  CreditCard, 
  Settings,
  LogOut,
  Building2
} from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';

interface SidebarProps {
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onLogout }) => {
  const location = useLocation();
  const { currentOrganization, isLoading } = useOrganization();

  const menuItems = useMemo(() => {
    const hasOrg = currentOrganization || isLoading;

    return [
      { id: 'organization', label: 'Organisations', icon: LayoutDashboard, path: `/organizations/create`, hidden: hasOrg },
      { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, path: `/${currentOrganization?.id}/dashboard`, hidden: !hasOrg },
      { id: 'entities', label: 'Entités', icon: Users, path: `/${currentOrganization?.id}/entities`, hidden: !hasOrg },
      { id: 'alerts', label: 'Alertes', icon: Shield, notifications: currentOrganization?.pending_alerts_count || 0, path: `/${currentOrganization?.id}/alerts`, hidden: !hasOrg },
      { id: 'billing', label: 'Facturation', icon: CreditCard, path: `/${currentOrganization?.id}/billing`, hidden: !hasOrg || currentOrganization?.role == 'member' },
      { id: 'settings', label: 'Paramètres', icon: Settings, path: '/settings', hidden: false },
    ]
  }, [currentOrganization, isLoading]);

  return (
    <div className="w-64 bg-slate-900 text-white flex flex-col h-screen">
      {/* Logo */}
      <div className="p-6 border-b border-slate-700">
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-blue-400" />
          <span className="text-xl font-bold">KYC Pro</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            if(item.hidden) {
              return null;
            }

            const Icon = item.icon;
            // Check if current location matches this menu item (accounting for org ID and sub-paths)
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <div className="flex items-center justify-between flex-1">
                    <span className="font-medium">{item.label}</span>
                    {item.notifications && item.notifications > 0 ? (
                      <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {item.notifications} 
                      </span>
                    ) : null}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-slate-700">
        <button
          onClick={onLogout}
          className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-600 hover:text-white transition-all duration-200"
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">Déconnexion</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;